// Predicate-head slot recognizer — predicate-head plan P2 (PH-D1/PH-D6/PH-D7).
// SYNTAX-ONLY: fires when the cursor sits at a bare boolean-expression start —
// after peeling back AT MOST ONE attached bare identifier prefix (`WHERE ord▮`
// fires; digit-led and completed-literal shapes do not), the preceding
// significant token is the clause head keyword (WHERE/HAVING/ON) or an
// AND/OR/NOT connective whose predicate chain before it is COMPLETE (a
// same-depth token-stack scan, not a last-token test). Clause/engine legality
// of the result is intellisense-owned (the PH-D2 capability gate); this layer
// does not consult the clause — mirroring `editorContextSlot.value.ts`.
//
// Explicitly NOT recognized here (PH-D6 refusals — each pinned in the corpus):
//   - `AND` under an unmatched same-depth BETWEEN (incl. multi-token low
//     bounds the value recognizer refuses) and window-frame extents — the
//     forward chain scan's pending-BETWEEN flag / barrier keywords refuse;
//   - `IS NOT ▮` (IS-chain NOT) and `x NOT ▮` (infix NOT LIKE/BETWEEN);
//   - `(` heads, `CASE [x] WHEN ▮` heads, and connectives whose chain-role
//     walk would cross OUT of the enclosing paren group — v1 gaps;
//   - `ON` without a genuine JOIN/MERGE association (C3 remediation —
//     `editorContextSlot.predicateHeadOn.ts`): upsert `ON ▮CONFLICT` /
//     `ON ▮DUPLICATE KEY`, JSON_TABLE `NULL ON ▮EMPTY`, object-target and
//     option-value `ON`s. The C1 gate bar alone missed the upsert shapes:
//     an `ON` met while the clause frame is `'from'` (`INSERT … SELECT …
//     FROM src ON ▮`) is NAMED `join-on` and passed the override path;
//   - string / comment / dollar-quote interiors (`isPredicateHeadOpaque`).
// The second `AND` after a COMPLETED BETWEEN fires. Disjointness with the
// scalar-value slot is bidirectional (PH-D7): `resolveSlot` runs the value
// scan FIRST, and this recognizer independently refuses every value-slot
// positive (their boundary token is an operator/`(`/`,` — never a head).
//
// Pure text scan (G11-rule): no tree-sitter, no intellisense imports.

import type { PredicateHeadInfo, PredicateHeadRole } from '../model/editorContext';
import { tokenizeUntil, type ClauseToken } from './editorContextClause';
import { hasJoinOrMergeAssociation } from './editorContextSlot.predicateHeadOn';
import { isPredicateHeadOpaque } from './editorContextSlot.predicateHeadScan';
import { isBareIdentStart } from './scanPrimitives';

// Keywords that, met at the connective's own paren depth BEFORE the containing
// head keyword, prove the connective does not continue a WHERE/HAVING/ON
// predicate chain (select-list booleans, CASE arms, window frames, DDL bodies
// …). Conservative by construction: a barrier refusal keeps today's
// classification. CASE/WHEN/THEN/ELSE make a chain holding a bare
// (unparenthesized) CASE expression refuse too — accepted v1 conservatism,
// consistent with the CASE-head gap; a parenthesized `(CASE … END)` operand
// is skipped as a balanced group and stays recognizable.
const CONNECTIVE_BARRIERS = new Set([
  'SELECT', 'FROM', 'JOIN', 'USING', 'SET', 'VALUES', 'RETURNING', 'INTO',
  'INSERT', 'UPDATE', 'DELETE', 'MERGE', 'CASE', 'WHEN', 'THEN', 'ELSE',
  'GROUP', 'ORDER', 'BY', 'LIMIT', 'OFFSET', 'FETCH', 'TOP', 'ROWS',
  'WITH', 'AS', 'UNION', 'INTERSECT', 'EXCEPT', 'CREATE', 'ALTER', 'DROP',
]);

// Keywords that terminate a predicate chain as a COMPLETE operand (`x IS NOT
// NULL AND ▮`, `CASE … END = 1` never reaches here bare — see barriers).
const COMPLETE_TAIL_KEYWORDS = new Set(['NULL', 'END']);

const HEAD_ROLES: ReadonlyMap<string, PredicateHeadRole> = new Map([
  ['WHERE', 'where-head'],
  ['HAVING', 'having-head'],
  ['ON', 'on-head'],
]);

export function recognizePredicateHead(args: {
  readonly source: string;
  readonly stmtStart: number;
  readonly offset: number;
  readonly engine: string;
}): PredicateHeadInfo | undefined {
  const { source, stmtStart, offset, engine } = args;
  if (offset < stmtStart || offset > source.length) return undefined;
  if (isPredicateHeadOpaque(source, stmtStart, offset, engine)) return undefined;

  const tokens = tokenizeUntil(source, stmtStart, offset, engine);
  if (tokens.length === 0) return undefined;

  // Peel back AT MOST ONE attached bare identifier prefix (PH-D6 — mirrors
  // the shipped run-skip at `editorContextSlot.value.ts:90-98`). Keyword-kind
  // tokens peel too (`WHERE not▮` is a prefix being typed). Digit-led runs
  // never peel: the tokenizer lexes them as a literal (`12▮`) or splits a
  // literal off the front (`1abc▮` → literal `1` + identifier `abc`), and a
  // literal tail refuses below. Quoted identifiers (`"ord▮`) fail the
  // bare-start test and refuse below.
  let last = tokens.length - 1;
  const tail = tokens[last]!;
  if (
    tail.end === offset &&
    (tail.kind === 'identifier' || tail.kind === 'keyword') &&
    isBareIdentStart(tail.text.charCodeAt(0))
  ) {
    last -= 1;
  }
  if (last < 0) return undefined;
  return classifyHeadAt(tokens, last, engine);
}

/** The preceding significant token sits at `tokens[last]`. Fires only when it
 *  is a head keyword or a valid connective (PH-D6). `ON` additionally needs a
 *  genuine JOIN/MERGE association (C3 remediation — `ON` is overloaded far
 *  beyond join predicates: upsert `ON CONFLICT` / `ON DUPLICATE KEY`,
 *  JSON_TABLE `ON EMPTY`/`ON ERROR`, FK actions, option values; see
 *  `editorContextSlot.predicateHeadOn.ts`; the walk is engine-aware — CROSS
 *  JOIN ownership and mysql's `STRAIGHT_JOIN` are dialect facts). */
function classifyHeadAt(
  tokens: readonly ClauseToken[],
  last: number,
  engine: string,
): PredicateHeadInfo | undefined {
  const tok = tokens[last]!;
  if (tok.kind !== 'keyword') return undefined;
  const kw = tok.text.toUpperCase();

  const headRole = HEAD_ROLES.get(kw);
  if (headRole !== undefined) {
    if (headRole === 'on-head' && !hasJoinOrMergeAssociation(tokens, last, engine)) return undefined;
    return { role: headRole, via: 'clause-keyword' };
  }

  if (kw === 'AND' || kw === 'OR') {
    const role = validateConnective(tokens, last, engine);
    if (role === undefined) return undefined;
    return { role, via: kw === 'AND' ? 'and' : 'or' };
  }

  if (kw === 'NOT') {
    // Unary-NOT chains (`NOT NOT ▮`) collapse; the token before the run
    // decides. `IS NOT ▮` is an IS-chain, `x NOT ▮` is infix NOT LIKE /
    // NOT BETWEEN territory, `(NOT ▮` inherits the `(`-head v1 gap — refuse.
    let i = last;
    while (i >= 0 && tokens[i]!.kind === 'keyword' && tokens[i]!.text.toUpperCase() === 'NOT') {
      i -= 1;
    }
    if (i < 0) return undefined;
    const prev = tokens[i]!;
    if (prev.kind !== 'keyword') return undefined;
    const pkw = prev.text.toUpperCase();
    if (pkw === 'IS') return undefined;
    const notHeadRole = HEAD_ROLES.get(pkw);
    if (notHeadRole !== undefined) {
      if (notHeadRole === 'on-head' && !hasJoinOrMergeAssociation(tokens, i, engine)) return undefined;
      return { role: notHeadRole, via: 'not' };
    }
    if (pkw === 'AND' || pkw === 'OR') {
      const role = validateConnective(tokens, i, engine);
      if (role === undefined) return undefined;
      return { role, via: 'not' };
    }
    return undefined;
  }

  return undefined;
}

/** PH-D6 token-stack validation for a connective at `tokens[connIdx]`.
 *  Backward pass: find the containing head keyword at the connective's own
 *  paren depth — a barrier keyword or crossing OUT of the enclosing group
 *  refuses (v1). Forward pass over the chain `[headIdx+1, connIdx)`: an
 *  unpaired BETWEEN (its joining AND not yet seen) means THIS connective is
 *  the BETWEEN's high-bound joiner — refuse; the chain's last same-depth
 *  token must be a complete operand (identifier / literal / `)` / NULL /
 *  END). Returns the inherited role, or undefined to refuse. */
function validateConnective(
  tokens: readonly ClauseToken[],
  connIdx: number,
  engine: string,
): PredicateHeadRole | undefined {
  let depth = 0;
  let headIdx = -1;
  let role: PredicateHeadRole | undefined;
  for (let i = connIdx - 1; i >= 0; i -= 1) {
    const tok = tokens[i]!;
    if (tok.kind === 'rparen') { depth += 1; continue; }
    if (tok.kind === 'lparen') {
      depth -= 1;
      if (depth < 0) return undefined;
      continue;
    }
    if (depth > 0 || tok.kind !== 'keyword') continue;
    const kw = tok.text.toUpperCase();
    const headRole = HEAD_ROLES.get(kw);
    if (headRole !== undefined) {
      // An inherited on-head needs the same JOIN/MERGE association as a
      // direct one (`… ON a.x = b.x AND ▮` inherits; an upsert-`ON` chain
      // never validates).
      if (headRole === 'on-head' && !hasJoinOrMergeAssociation(tokens, i, engine)) return undefined;
      role = headRole; headIdx = i; break;
    }
    if (CONNECTIVE_BARRIERS.has(kw)) return undefined;
  }
  if (role === undefined) return undefined;

  let chainDepth = 0;
  let pendingBetween = false;
  let lastTail: ClauseToken | undefined;
  for (let i = headIdx + 1; i < connIdx; i += 1) {
    const tok = tokens[i]!;
    if (tok.kind === 'lparen') { chainDepth += 1; lastTail = tok; continue; }
    if (tok.kind === 'rparen') { chainDepth -= 1; lastTail = tok; continue; }
    if (chainDepth > 0) continue; // nested-group interiors are invisible
    lastTail = tok;
    if (tok.kind === 'keyword') {
      const kw = tok.text.toUpperCase();
      if (kw === 'BETWEEN') pendingBetween = true;
      else if (kw === 'AND' && pendingBetween) pendingBetween = false;
    }
  }
  if (pendingBetween) return undefined;
  if (lastTail === undefined) return undefined; // empty chain (`WHERE AND ▮`)
  if (!isCompleteChainTail(lastTail)) return undefined;
  return role;
}

function isCompleteChainTail(tok: ClauseToken): boolean {
  if (tok.kind === 'identifier' || tok.kind === 'literal' || tok.kind === 'rparen') return true;
  return tok.kind === 'keyword' && COMPLETE_TAIL_KEYWORDS.has(tok.text.toUpperCase());
}
